import React, { useState } from 'react';

import {
  Menu,
  MenuItem,
  MenuItemLabel,
  Pressable,
  Avatar,
  AvatarImage,
  AvatarFallbackText,
  Icon,
  Text,
  Box,
  Divider,
  HStack,
  VStack,
} from '@gluestack-ui/themed';
import {
  KeyRound,
  LogOut,
  UserRound,
  Users,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { User } from '@/common/api/models/user';
import { useListConnections } from '@/common/api/endpoints/connections/connections';
import { useGetPeopleProfilePhotoUrl } from '@/common/api/endpoints/users/users';
import type { ConnectionView } from '@/common/api/models/connectionView';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useStudentPeopleNavigation } from '@/student/people/StudentPeopleNavigationContext';
import { requestExamLeave } from '@/common/components/meeting/ExamLeaveGuard';
import { ChangePasswordModal } from './ChangePasswordModal';

interface UserMenuProps {
  user: User | null;
  logout: () => void;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'top left' | 'top right' | 'bottom left' | 'bottom right' | 'right top' | 'right bottom' | 'left top' | 'left bottom';
}

export function isHttpUrl(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

/** Photo SAS first, then a real http(s) `user.avatar`. Non-URL seeds must not hide initials. */
export function resolveUserMenuAvatarUri(
  photoUrl: string | null | undefined,
  userAvatar: string | null | undefined,
): string | undefined {
  if (isHttpUrl(photoUrl)) return photoUrl;
  if (isHttpUrl(userAvatar)) return userAvatar;
  return undefined;
}

export function countPendingInConnections(
  connections: Array<Pick<ConnectionView, 'direction'>> | null | undefined,
): number {
  return (connections ?? []).filter((connection) => connection.direction === 'pending_in').length;
}

export function formatUnreadBadgeLabel(count: number): string {
  return count > 9 ? '9+' : String(count);
}

/** Initials for AvatarFallbackText — pass letters, not the full name. */
export function userMenuInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function UnreadCountBadge({
  count,
  testID,
}: {
  count: number;
  testID: string;
}) {
  if (count <= 0) return null;
  const unreadLabel = formatUnreadBadgeLabel(count);
  return (
    <Box
      bg="$statusError"
      borderRadius="$full"
      minWidth={18}
      h={18}
      px="$1"
      alignItems="center"
      justifyContent="center"
      testID={testID}
      accessibilityLabel={`${unreadLabel} unread connection requests`}
    >
      <Text size="xs" color="$textOnError" fontWeight="bold" lineHeight={14}>
        {unreadLabel}
      </Text>
    </Box>
  );
}

export function UserMenuAvatarButton({
  triggerProps,
  avatarUri,
  fallbackName,
  unreadCount,
}: {
  triggerProps?: Record<string, unknown>;
  avatarUri?: string;
  fallbackName: string;
  unreadCount: number;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  React.useEffect(() => {
    setImageFailed(false);
  }, [avatarUri]);

  const showPhoto = Boolean(avatarUri) && !imageFailed;
  const initials = userMenuInitials(fallbackName);

  return (
    <Pressable testID="UserMenuAvatar" accessibilityLabel={fallbackName} {...triggerProps}>
      <Box position="relative">
        <Avatar bg="$brandBase" size="sm" borderRadius="$full" testID="user-menu-avatar">
          {showPhoto ? (
            <AvatarImage
              source={{ uri: avatarUri }}
              alt=""
              testID="user-menu-avatar-image"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <AvatarFallbackText color="$textOnBrand" testID="user-menu-avatar-initials">
              {initials}
            </AvatarFallbackText>
          )}
        </Avatar>
        {unreadCount > 0 ? (
          <Box
            position="absolute"
            top={-4}
            right={-4}
            borderWidth={1.5}
            borderColor="$bgCard"
            borderRadius="$full"
            testID="user-menu-unread-badge"
          >
            <UnreadCountBadge count={unreadCount} testID="user-menu-unread-badge-label" />
          </Box>
        ) : null}
      </Box>
    </Pressable>
  );
}

export const UserMenu: React.FC<UserMenuProps> = ({ user, logout, placement }) => {
  const { t } = useTranslation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { isFeatureActive } = useUserFeatures();
  const peopleNav = useStudentPeopleNavigation();

  const showPeopleMenu = user?.role === 'student' && isFeatureActive('student-connections');
  const userId = user?.id ?? '';
  const blobPath = user?.profilePhotoBlobPath ?? null;

  const photoUrlQuery = useGetPeopleProfilePhotoUrl(userId, {
    query: { enabled: showPeopleMenu && !!blobPath },
  });

  const connectionsQuery = useListConnections({
    query: {
      enabled: showPeopleMenu,
      refetchInterval: showPeopleMenu ? 30_000 : false,
    },
  });

  if (!user) return null;

  const activeFlags = Object.entries(user.features || {})
    .filter(([, value]) => value === true)
    .map(([key]) => key);

  const avatarUri = resolveUserMenuAvatarUri(photoUrlQuery.data?.data?.data?.url, user.avatar);
  const fallbackName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.username || 'U';
  const unreadCount = countPendingInConnections(connectionsQuery.data?.data?.data);

  return (
    <>
      <Menu
        placement={placement}
        offset={10}
        minWidth={280}
        trigger={({ ...triggerProps }) => (
          <UserMenuAvatarButton
            triggerProps={triggerProps}
            avatarUri={avatarUri}
            fallbackName={fallbackName}
            unreadCount={unreadCount}
          />
        )}
      >
        <MenuItem
          key="AccountHeader"
          textValue={fallbackName}
          testID="user-menu-account-header"
          disabled
          closeOnSelect={false}
        >
          <HStack space="sm" alignItems="center" py="$1" pr="$2" maxWidth={240}>
            <Avatar bg="$brandBase" size="sm" borderRadius="$full">
              {avatarUri ? (
                <AvatarImage source={{ uri: avatarUri }} alt="" />
              ) : (
                <AvatarFallbackText color="$textOnBrand">
                  {userMenuInitials(fallbackName)}
                </AvatarFallbackText>
              )}
            </Avatar>
            <VStack flex={1}>
              <Text size="sm" fontWeight="semibold" color="$textHeading" numberOfLines={1}>
                {fallbackName}
              </Text>
              {user.email ? (
                <Text size="xs" color="$textSubtle" numberOfLines={1}>
                  {user.email}
                </Text>
              ) : null}
            </VStack>
          </HStack>
        </MenuItem>
        {showPeopleMenu && (
          <>
            <MenuItem key="div-people" textValue="separator" disabled closeOnSelect={false} p="$0">
              <Divider bg="$borderLight" w="$full" />
            </MenuItem>
            <MenuItem
              key="Profile"
              textValue="Profile"
              testID="menu-item-profile"
              onPress={() => peopleNav?.navigateToProfile()}
            >
              <Icon as={UserRound} size="sm" color="$textBody" mr="$2.5" />
              <MenuItemLabel size="sm" color="$textBody">
                {t('navigation.sidebar.menu.profile')}
              </MenuItemLabel>
            </MenuItem>
            <MenuItem
              key="MyConnections"
              textValue="My connections"
              testID="menu-item-my-connections"
              onPress={() => peopleNav?.navigateToConnections()}
            >
              <Icon as={Users} size="sm" color="$textBody" mr="$2.5" />
              <MenuItemLabel size="sm" color="$textBody" flex={1}>
                {t('navigation.sidebar.menu.myConnections')}
              </MenuItemLabel>
              <UnreadCountBadge count={unreadCount} testID="menu-item-my-connections-unread" />
            </MenuItem>
          </>
        )}
        <MenuItem key="div-account" textValue="separator" disabled closeOnSelect={false} p="$0">
          <Divider bg="$borderLight" w="$full" />
        </MenuItem>
        <MenuItem
          key="ChangePassword"
          textValue="Change Password"
          testID="menu-item-change-password"
          onPress={() => setShowChangePassword(true)}
        >
          <Icon as={KeyRound} size="sm" color="$textBody" mr="$2.5" />
          <MenuItemLabel size="sm" color="$textBody">
            {t('navigation.sidebar.menu.change-password')}
          </MenuItemLabel>
        </MenuItem>
        <MenuItem
          key="Logout"
          textValue="Logout"
          testID="menu-item-logout"
          onPress={() => {
            requestExamLeave().then((ok) => {
              if (ok) {
                logout();
              }
            }).catch(() => {});
          }}
        >
          <Icon as={LogOut} size="sm" color="$statusError" mr="$2.5" />
          <MenuItemLabel size="sm" color="$statusError">
            {t('navigation.sidebar.menu.logout')}
          </MenuItemLabel>
        </MenuItem>
        {activeFlags.length > 0 && (
          <>
            <MenuItem key="div-debug" textValue="separator" disabled closeOnSelect={false} p="$0">
              <Divider bg="$borderLight" w="$full" />
            </MenuItem>
            <MenuItem key="DebugFlags" textValue="Debug Flags" closeOnSelect={false} disabled>
              <Box w="$full" px="$1" py="$1">
                <Text size="xs" color="$textHint">
                  {activeFlags.join(', ')}
                </Text>
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>

      <ChangePasswordModal isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </>
  );
};

export default UserMenu;
